import { z } from 'zod'

import band68 from '../data/words/reading-6-8.json' with { type: 'json' }
import band911 from '../data/words/reading-9-11.json' with { type: 'json' }
import band12 from '../data/words/reading-12-plus.json' with { type: 'json' }
import type { Rng } from './rng.js'

/**
 * Reading bands rather than school years.
 *
 * She should be able to move up when she is ready rather than when a birthday
 * says so, and move back down without it feeling like a demotion.
 */
export const READING_BANDS = ['6-8', '9-11', '12+'] as const
export type ReadingBand = (typeof READING_BANDS)[number]

export const DEFAULT_BAND: ReadingBand = '6-8'

export const wordEntrySchema = z.object({
  /** Lower case, letters only — the games uppercase for display. */
  word: z.string().regex(/^[a-z]+$/),
  /** A nudge for a stuck player, so being stuck is not just losing slowly. */
  hint: z.string().min(1),
})

export type WordEntry = z.infer<typeof wordEntrySchema>

const BANDS: Readonly<Record<ReadingBand, readonly WordEntry[]>> = {
  '6-8': band68,
  '9-11': band911,
  '12+': band12,
}

export function isReadingBand(value: string): value is ReadingBand {
  return (READING_BANDS as readonly string[]).includes(value)
}

/** Resolves a stored band setting, falling back rather than throwing. */
export function toReadingBand(value: string | null | undefined): ReadingBand {
  return typeof value === 'string' && isReadingBand(value) ? value : DEFAULT_BAND
}

export function wordsIn(band: ReadingBand): readonly WordEntry[] {
  return BANDS[band]
}

export interface PickWordOptions {
  /** Words already used this session, so a sitting does not repeat itself. */
  readonly exclude?: ReadonlySet<string>
}

/**
 * Chooses a word, honouring the injected RNG so a game is reproducible.
 *
 * When every word in a band has been used the exclusions are ignored rather
 * than failing — running out of words should start the list again, not end
 * the game.
 */
export function pickWord(band: ReadingBand, rng: Rng, options: PickWordOptions = {}): WordEntry {
  const all = wordsIn(band)
  const exclude = options.exclude

  const available = exclude ? all.filter((entry) => !exclude.has(entry.word)) : all

  return rng.pick(available.length > 0 ? available : all)
}
